import { AlertCircle, ArrowLeft, Calendar, CheckCircle, Zap } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CategorySelector } from '@/components/CategorySelector';
import { ApiError } from '@/lib/apiClient';
import { cn } from '@/lib/cn';
import { useChallengeCapacity, useCreateChallenge } from '@/lib/challenges';

/** Create a public challenge through the real D1-backed API. */
export function CreateChallengePage() {
  const navigate = useNavigate();
  const createChallenge = useCreateChallenge();
  const capacityQuery = useChallengeCapacity();
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [category, setCategory] = useState('Photography');
  const [endsAt, setEndsAt] = useState(getDefaultDeadline(7));
  const [createdId, setCreatedId] = useState<string | null>(null);

  const titleError = useMemo(() => {
    if (!title.trim()) return 'Title is required';
    if (title.trim().length < 3) return 'Title must be at least 3 characters';
    if (title.length > 120) return 'Title must be 120 characters or less';
    return '';
  }, [title]);

  const briefError = useMemo(() => {
    if (!brief.trim()) return 'Description is required';
    if (brief.trim().length < 10) return 'Description must be at least 10 characters';
    if (brief.length > 500) return 'Description must be 500 characters or less';
    return '';
  }, [brief]);

  const deadlineError = useMemo(() => {
    const deadline = new Date(endsAt);
    if (Number.isNaN(deadline.getTime())) return 'Choose a valid deadline';
    if (deadline.getTime() < Date.now() + 60 * 60 * 1000) {
      return 'Deadline must be at least 1 hour from now';
    }
    if (deadline.getTime() > Date.now() + 30 * 24 * 60 * 60 * 1000) {
      return 'Deadline cannot be more than 30 days away';
    }
    return '';
  }, [endsAt]);

  const capacity = capacityQuery.data;
  const atChallengeLimit = capacity?.canCreate === false;
  const canSubmit = !titleError && !briefError && !deadlineError && !atChallengeLimit;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    try {
      const { challenge } = await createChallenge.mutateAsync({
        title: title.trim(),
        brief: brief.trim(),
        category: category as 'Photography' | 'Cooking' | 'Singing' | 'Cars' | 'Music',
        endsAt,
      });
      setCreatedId(challenge.id);
    } catch {
      // Mutation state renders a safe server-provided error below.
    }
  };

  if (createdId) {
    return (
      <div className="from-brand-50 min-h-screen bg-gradient-to-br to-slate-50 px-4 py-12">
        <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
          <h1 className="mt-5 text-2xl font-bold text-slate-900">Challenge created!</h1>
          <p className="mt-2 text-slate-600">Your challenge is live and ready for submissions.</p>
          <div className="mt-8 flex gap-3 border-t border-slate-200 pt-5">
            <button type="button" onClick={() => navigate('/')} className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 font-semibold text-slate-900 hover:bg-slate-50">
              Back to home
            </button>
            <button type="button" onClick={() => navigate(`/challenges/${createdId}`)} className="from-brand-500 to-brand-700 flex-1 rounded-lg bg-gradient-to-br px-4 py-2.5 font-semibold text-white">
              View challenge
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="from-brand-50 min-h-screen bg-gradient-to-br to-slate-50 px-4 py-12">
      <div className="mx-auto max-w-2xl">
        <button type="button" onClick={() => navigate('/')} className="mb-6 flex items-center gap-2 text-sm font-semibold text-brand-600 hover:text-brand-700">
          <ArrowLeft className="h-4 w-4" /> Back to challenges
        </button>
        <div className="mb-8">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Create a challenge</h1>
              <p className="mt-2 text-slate-600">Start a public challenge. Private/invite-only challenges will be added after the core public flow is persistent.</p>
            </div>
            <div className={cn(
              'rounded-full border px-3 py-1.5 text-sm font-semibold',
              atChallengeLimit
                ? 'border-red-200 bg-red-50 text-red-700'
                : capacity?.activeCount === (capacity?.maxActive ?? 5) - 1
                  ? 'border-amber-200 bg-amber-50 text-amber-700'
                  : 'border-slate-200 bg-white text-slate-700',
            )}>
              {capacityQuery.isLoading
                ? 'Active challenges: …/5'
                : `Active challenges: ${capacity?.activeCount ?? 0}/${capacity?.maxActive ?? 5}`}
            </div>
          </div>
        </div>

        {capacity && capacity.activeCount === capacity.maxActive - 1 && (
          <div className="mb-5 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">You have one active challenge slot remaining. Ended challenges automatically free a slot.</p>
          </div>
        )}

        {atChallengeLimit && (
          <div className="mb-5 flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <div className="text-sm text-red-800">
              <p className="font-semibold">You have reached the 5 active challenge limit.</p>
              <p className="mt-1">Wait for a challenge to end, or open one of your challenges and delete it to free a slot immediately.</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label htmlFor="challenge-title" className="text-sm font-medium text-slate-900">Title</label>
              <span className="text-xs text-slate-500">{title.length}/120</span>
            </div>
            <input id="challenge-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="E.g., Best Coffee Shop Photo" disabled={atChallengeLimit} className={cn('w-full rounded-lg border px-4 py-3 text-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-100', titleError ? 'border-red-300 focus:ring-red-200' : 'border-slate-200 focus:ring-brand-200')} />
            {titleError && <p className="mt-1 text-xs text-red-600">{titleError}</p>}
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label htmlFor="challenge-brief" className="text-sm font-medium text-slate-900">Description</label>
              <span className="text-xs text-slate-500">{brief.length}/500</span>
            </div>
            <textarea id="challenge-brief" value={brief} onChange={(e) => setBrief(e.target.value)} maxLength={500} rows={5} placeholder="What should people submit? Include any rules or guidelines." disabled={atChallengeLimit} className={cn('w-full resize-none rounded-lg border px-4 py-3 text-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-100', briefError ? 'border-red-300 focus:ring-red-200' : 'border-slate-200 focus:ring-brand-200')} />
            {briefError && <p className="mt-1 text-xs text-red-600">{briefError}</p>}
          </div>

          <div>
            <label className="mb-3 block text-sm font-medium text-slate-900">Category</label>
            <CategorySelector value={category} onChange={setCategory} />
          </div>

          <div>
            <label htmlFor="challenge-deadline" className="mb-2 block text-sm font-medium text-slate-900">Deadline</label>
            <div className="relative">
              <Calendar className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <input id="challenge-deadline" type="datetime-local" value={toLocalDateTimeValue(endsAt)} min={toLocalDateTimeValue(new Date(Date.now() + 60 * 60 * 1000).toISOString())} max={toLocalDateTimeValue(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString())} onChange={(e) => setEndsAt(new Date(e.target.value).toISOString())} disabled={atChallengeLimit} className={cn('w-full rounded-lg border py-3 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-100', deadlineError ? 'border-red-300 focus:ring-red-200' : 'border-slate-200 focus:ring-brand-200')} />
            </div>
            {deadlineError && <p className="mt-1 text-xs text-red-600">{deadlineError}</p>}
          </div>

          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">Challenges and future submissions must follow Jemmi's content guidelines. Media moderation is required before public uploads are enabled.</p>
          </div>

          {createChallenge.isError && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {createChallenge.error instanceof ApiError
                ? createChallenge.error.message
                : 'Unable to create the challenge. Check that you are signed in and try again.'}
            </div>
          )}

          <button type="submit" disabled={!canSubmit || createChallenge.isPending || capacityQuery.isLoading} className={cn('flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold text-white transition-all', canSubmit && !createChallenge.isPending && !capacityQuery.isLoading ? 'from-brand-500 to-brand-700 bg-gradient-to-br hover:shadow-md' : 'cursor-not-allowed bg-slate-300')}>
            {atChallengeLimit ? 'Challenge limit reached' : createChallenge.isPending ? 'Creating…' : 'Create challenge'}
            {!atChallengeLimit && !createChallenge.isPending && <Zap className="h-4 w-4" />}
          </button>
        </form>
      </div>
    </div>
  );
}

function getDefaultDeadline(days: number): string {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

function toLocalDateTimeValue(iso: string): string {
  const date = new Date(iso);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}
